import Dexie from 'dexie';

/**
 * BabyTrack offline-first DB.
 *
 * Per shared-docs/offline-first-architecture.md §"Step 2". Single Dexie
 * instance, one `events` table covering all entry types (FEED / SLEEP /
 * NAPPY / ACTIVITY / PUMPING / …), a mutation queue, and a key-value meta
 * store. Auth scoping is via `userId`; baby scoping is via `babyId`.
 *
 * Indexes (after the primary key):
 *   events:        userId, babyId, [babyId+type], syncStatus, deletedAt, startTime
 *   mutationQueue: status, entity, createdAt
 *   meta:          (only key)
 */
class BabyTrackDB extends Dexie {
  constructor() {
    super('babytrack-db');
    this.version(1).stores({
      events:
        'eventId, userId, babyId, [babyId+type], syncStatus, deletedAt, startTime',
      mutationQueue: '++id, status, entity, createdAt',
      meta: 'key',
    });
    // v2 adds `recordId` index — required by queue.rewriteRecordId() and
    // router.routeDelete() which both do .where('recordId').equals(...).
    // Without the index, those throw "KeyPath ... not indexed", the post-
    // create rewrite fails, the queue entry is retried, and every successful
    // POST creates a duplicate row on the server (up to attempts=8).
    this.version(2).stores({
      mutationQueue: '++id, status, entity, createdAt, recordId',
    });

    this.events = this.table('events');
    this.mutationQueue = this.table('mutationQueue');
    this.meta = this.table('meta');
  }
}

let dbInstance = null;

export function getDB() {
  if (!dbInstance) dbInstance = new BabyTrackDB();
  return dbInstance;
}

export async function clearAllForUser(userId) {
  const db = getDB();
  await db.transaction('rw', db.events, db.mutationQueue, async () => {
    await db.events.where('userId').equals(userId).delete();
    await db.mutationQueue.clear();
  });
}

/**
 * Wipe every local table. Called on sign-out and account deletion.
 *
 * Deliberately a BLANKET clear rather than clearAllForUser(userId): rows pulled
 * from the server carry the id of whoever LOGGED the event, not the viewer's,
 * and listForBaby reads by babyId alone. A userId-scoped delete therefore left
 * most rows behind, and the next person to sign in on the same device — the
 * other parent, or anyone on a shared iPad — read them straight out of Dexie
 * before any network call.
 *
 * Never throws: failing to clear must not strand someone in a half-signed-out
 * state where they can't get to the login screen.
 */
export async function clearAllLocalData() {
  try {
    const db = getDB();
    await db.transaction('rw', db.events, db.mutationQueue, db.meta, async () => {
      await db.events.clear();
      await db.mutationQueue.clear();
      await db.meta.clear();
    });
  } catch (e) {
    console.error('[db] clearAllLocalData failed:', e?.message);
  }
}
